<script lang="ts">
	import {
		BaseCartesianChart,
		CartesianChartFromType,
		LineChart,
		PieChart,
		type ChartDataInterface,
	} from '@Shelly/shelly-design-system';
	import { applyChartSeriesDisplayNames } from 'src/helpers/statistics/ui/chart_series_labels';
	import LayerChartSpacing from './LayerChartSpacing.svelte';

	export let chartData: ChartDataInterface;
	export let thumbMode = false;
	/** Skip drawer-style outer spacing when the chart already sits in a ChartCard shell. */
	export let disableOuterSpacing = false;
	export let legend: boolean | null | undefined = null;
	export let fullScreen = false;
	export let showGradient: boolean | null | undefined = null;
	export let colorSchema: 'red' | 'blue' | 'green' | 'purple' | 'gold' | null | undefined = null;
	export let colorScheme: string[] | null | undefined = null;
	export let showPieLegend = !thumbMode;
	export let pieCenterLabel = 'Total';

	type TemperatureColorSchema = 'red' | 'blue' | undefined;

	const getTemperatureColors = (
		chartData: ChartDataInterface,
	): { colorSchema: TemperatureColorSchema; colorScheme: string[] | undefined } => {
		const seriesStyle = chartData.series?.[0]?.style;
		if (seriesStyle === 'tempSchedule_heat') {
			return { colorSchema: 'red', colorScheme: ['#ff4e60'] };
		}
		if (seriesStyle === 'tempSchedule_cool') {
			return { colorSchema: 'blue', colorScheme: ['#248def'] };
		}
		return { colorSchema: undefined, colorScheme: undefined };
	};
	const isZeroConsumptionChart = (chartData: ChartDataInterface) => {
		if (!/consumption/i.test(chartData.name)) return false;

		const points = chartData.series
			.filter((series) => series.visible !== false)
			.flatMap((series) => series.data || []);
		return points.length > 0 && points.every((point) => Number(point.value ?? 0) === 0);
	};

	$: temperatureColors = getTemperatureColors(chartData);
	$: showZeroConsumptionLine = isZeroConsumptionChart(chartData);
	$: presentedChartData = applyChartSeriesDisplayNames(chartData);
	$: resolvedShowGradient = showGradient ?? false;
	$: useFigmaAreaFade = !thumbMode && !showZeroConsumptionLine;
	$: resolvedColorMode =
		colorSchema || colorScheme?.length ? ('semantic' as const) : ('family' as const);
</script>

<LayerChartSpacing enabled={!thumbMode && chartData.type !== 'pie' && !disableOuterSpacing}>
	{#if chartData.type === 'pie'}
		<PieChart
			chartData={presentedChartData}
			showLegend={showPieLegend}
			centerLabel={pieCenterLabel}
		/>
	{:else if /^(tempSchedule|tariffIntervals)$/.test(chartData.name)}
		<BaseCartesianChart
			chartData={presentedChartData}
			mark="area"
			curve="step"
			showPoints={!thumbMode}
			legend={legend ?? undefined}
			{fullScreen}
			showGradient={resolvedShowGradient}
			surfaceGradient={null}
			areaGradient={useFigmaAreaFade}
			colorMode={resolvedColorMode}
			colorSchema={colorSchema ?? temperatureColors.colorSchema}
			colorScheme={colorScheme ?? temperatureColors.colorScheme}
		/>
	{:else if showZeroConsumptionLine}
		{#if thumbMode}
			<CartesianChartFromType
				chartData={presentedChartData}
				{thumbMode}
				legend={legend ?? undefined}
				{fullScreen}
				colorSchema="green"
				colorScheme={['#25b684']}
			/>
		{:else}
			<LineChart
				chartData={presentedChartData}
				showPoints={false}
				legend={legend ?? undefined}
				{fullScreen}
				colorSchema="green"
				colorScheme={['#25b684']}
			/>
		{/if}
	{:else}
		<CartesianChartFromType
			chartData={presentedChartData}
			{thumbMode}
			legend={legend ?? undefined}
			{fullScreen}
			showGradient={resolvedShowGradient}
			surfaceGradient={null}
			areaGradient={useFigmaAreaFade}
			colorMode={resolvedColorMode}
			colorSchema={colorSchema ?? undefined}
			colorScheme={colorScheme ?? undefined}
		/>
	{/if}
</LayerChartSpacing>
